import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import ApprovalService from "../../../../services/order/approval-service";
import { ApplicationSort } from "../../../../models/shared/application-sort";
import { RootState } from "../../../store";
import { ApplicationError } from "../../../../models/errors/application-error";
import { ApplicationOrderLineList } from "../../../../models/order/application-order-line-list";
import { ApplicationOrderList } from "../../../../models/order/application-order-list";
import { ApplicationOrderLine } from "../../../../models/order/application-order-line";
import SupplierService from "../../../../services/supplier/supplier-service";
import { ApplicationSupplierList } from "../../../../models/supplier/application-supplier-list";
import { ApplicationSupplierProductHeader } from "../../../../models/supplier/application-supplier-product-header";
import OrderService from "../../../../services/order/order-service";
import { ApplicationOrderStatus } from "../../../../models/order/application-order-status";
import { ApplicationOrder } from "../../../../models/order/application-order";
import { showSuccess } from "../../../../components/notification/toastr-actions";
import { NavigateFunction } from "react-router-dom";
import { ApplicationApprovalStatus } from "../../../../models/order/application-approval-status";
import { ApplicationOrderLineSplit } from "../../../../models/order/application-order-line-split";
import { ApplicationApprovalFilter } from "../../../../models/order/approval/application-approval-filter";
import { ApplicationSupplierHeader } from "../../../../models/supplier/application-supplier-header";
import { FetchParams } from "../../../../components/controls/table-view";
import { ApplicationDepartment } from "../../../../models/organisation/application-department";
import { ApplicationApprovalOrderFilter } from "../../../../models/order/approval/application-approval-order-filter";
import { ApplicationBasketTotals } from "../../../../models/order/basket/application-basket-totals";
import { ApplicationProductCostPriceHistory } from "../../../../models/product/application-product-cost-price-history";
import ProductService from "../../../../services/product/product-service";


export const fetchApprovalOrders = createAsyncThunk<ApplicationOrderList | null, { departmentId: number, fetchParams?: FetchParams, filter?: ApplicationApprovalOrderFilter }, { state: RootState }>(
    'approval/getorders',
    async ({ departmentId, fetchParams, filter }, { getState, rejectWithValue, dispatch }) => {

        const state = getState();
        const approvalService = new ApprovalService();
        try {
            var currentPosition: number = state.approval?.approvalOrders?.currentPosition ?? 0;
            var fetchNext: number = 15;
            var count: number = state.approval.approvalOrders?.count ?? -1;

            if (fetchParams?.reset) {
                currentPosition = 0;
                count = -1;
                dispatch(resetApprovals());
            }

            if (currentPosition !== count) {
                return await approvalService.getOrders(departmentId, currentPosition, fetchNext, ApplicationSort.Asc, filter)
            }
            return null;
        } catch (error: any) {

            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);


export const fetchApprovals = createAsyncThunk<ApplicationOrderLineList | null, { orderId: number, fetchParams: FetchParams, filters?: ApplicationApprovalFilter }, { state: RootState }>(
    'approval/get',
    async ({ orderId, fetchParams, filters }, { dispatch, getState, rejectWithValue }) => {

        const state = getState();
        const approvalService = new ApprovalService();
        try {
            var currentPosition: number = state.approval?.approvals?.currentPosition ?? 0;
            var fetchNext: number = 50;
            var count: number = state.approval.approvals?.count ?? -1;

            if (fetchParams.reset) {
                currentPosition = 0;
                count = -1;
                dispatch(resetApprovalLines())
            }

            if (currentPosition !== count) {
                dispatch(getAwaitingApprovalCount({ orderId }))
                return await approvalService.get(orderId, currentPosition, fetchNext, fetchParams?.sortOrder ?? ApplicationSort.Asc, fetchParams.sortColumn, filters, fetchParams.searchText)
            }
            return null;
        } catch (error: any) {

            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const updateApproval = createAsyncThunk<
    ApplicationOrderLine,
    UpdateOrderLinePayload,
    { state: RootState }
>(
    "approval/update",
    async (orderLine, { getState, dispatch, rejectWithValue }) => {
        const approvalService = new ApprovalService();
        try {
            const { id, field, value } = orderLine;
            const state = getState();
            const foundLine = state.approval.approvals?.orderLines.find(
                (p) => p.id === id
            );

            if (!foundLine) {
                throw new ApplicationError("Cant find orderline");
            }

            const updatedLine = { ...foundLine, [field]: value };

            const response = await approvalService.updateApproval(updatedLine);

            if (field === "approvalStatus") {
                dispatch(getAwaitingApprovalCount({ orderId: response.orderId }));
            }
            dispatch(fetchApprovalTotals(response.orderId));

            return response;
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const fetchApprovalTotals = createAsyncThunk<
    ApplicationBasketTotals,
    number,
    { state: RootState }
>(
    "approval/totals",
    async (orderId, { rejectWithValue }) => {
        const approvalService = new ApprovalService();
        try {
            return await approvalService.getTotals(orderId);
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);


export const fetchApproval = createAsyncThunk<
    ApplicationOrderLine,
    { orderLineId: number },
    { state: RootState }
>(
    'approval/fetchByID',
    async ({ orderLineId }, { rejectWithValue }) => {
        const approvalService = new ApprovalService();
        try {
            return await approvalService.getById(orderLineId); // send full object
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const fetchPriceHistory = createAsyncThunk<
    ApplicationProductCostPriceHistory,
    { productId: number, branchId: number },
    { state: RootState }
>(
    'approval/fetchPriceHistory',
    async ({ productId, branchId }, { rejectWithValue }) => {
        const productService = new ProductService();
        try {
            return await productService.getProductPriceHistory(productId, branchId);
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);


export const fetchOrderSuppliers = createAsyncThunk<
    ApplicationSupplierHeader[],
    { orderId: number },
    { state: RootState }
>(
    'approval/suppliers',
    async ({ orderId }, { rejectWithValue }) => {
        const supplierService = new SupplierService();
        try {
            return await supplierService.getOrderSuppliers(orderId); // send full object
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const createSplit = createAsyncThunk<
    ApplicationOrderLine,
    { orderId: number, orderLineId: number, split: ApplicationOrderLineSplit[] },
    { state: RootState }
>(
    'approval/split',
    async ({ orderId, orderLineId, split }, { dispatch, rejectWithValue }) => {
        const orderService = new OrderService();
        try {
            const response = await orderService.createSplit(orderId, orderLineId, split);
            dispatch(fetchApprovalTotals(orderId));
            return response;
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const fetchSuppliers = createAsyncThunk<ApplicationSupplierList | null, void, { state: RootState }>(
    'suppliers/get',
    async (_, { rejectWithValue }) => {
        const supplierService = new SupplierService();
        try {

            return await supplierService.getSuppliers(-1, 0, true, "");
        } catch (error: any) {

            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);


export const getCommonNotes = createAsyncThunk<
  string[],
  { productId: number; supplierId: number } ,
  { state: RootState }
>("order/validateBasket", async ({productId, supplierId}, { rejectWithValue }) => {
  const orderService = new OrderService();
  try {
    return await orderService.getCommonNotes(productId, supplierId);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const fetchProductSuppliers = createAsyncThunk<Record<number, ApplicationSupplierProductHeader[]>, { branchId: number; productIds: number[] } | { branchId: number; productId: number }, { state: RootState }>(
    'suppliers/products/get',
    async (args, { getState, rejectWithValue }) => {
        const state = getState();
        const branchId = args.branchId;
        const productIds = 'productIds' in args ? args.productIds : [args.productId];
        const supplierService = new SupplierService();

        if (productIds.length === 1) {
            const productId = productIds[0] as number;
            const cached = state.approval.productSuppliers[productId];
            if (cached && cached.length > 0) {
                return { [productId]: cached } as Record<number, ApplicationSupplierProductHeader[]>;
            }
        }

        try {
            return await supplierService.getProductSuppliers(branchId, productIds);
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const approveOrder = createAsyncThunk<
    ApplicationOrder,
    { orderId: number; navigate: NavigateFunction },
    { state: RootState }
>(
    'approval/update/approve',
    async ({ orderId, navigate }, { rejectWithValue, dispatch }) => {
        const orderService = new OrderService();
        try {
            const order = await orderService.updateOrderStatus(
                orderId,
                ApplicationOrderStatus.Active
            );
            dispatch(showSuccess(`Order #${order.id} approved`));
            navigate(`/admin/approvals`, { replace: true });
            return order;
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const updateStatus = createAsyncThunk<
    void,
    { orderId: number, status: ApplicationApprovalStatus, filters?: ApplicationApprovalFilter },
    { state: RootState }
>(
    'approval/update/approveAll',
    async ({ orderId, status, filters }, { dispatch, rejectWithValue }) => {
        const approvalService = new ApprovalService();
        try {
            await approvalService.updateStatus(orderId, status, filters);
            dispatch(resetApprovalLines());
            dispatch(fetchApprovals({ orderId, fetchParams: {}, filters }));
            dispatch(getAwaitingApprovalCount({ orderId }))
            dispatch(fetchApprovalTotals(orderId));
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const getAwaitingApprovalCount = createAsyncThunk<
    number,
    { orderId: number },
    { state: RootState }
>(
    'approval/status/count',
    async ({ orderId }, { rejectWithValue }) => {
        const approvalService = new ApprovalService();
        try {
            return await approvalService.count(orderId, ApplicationApprovalStatus.AwaitingApproval);
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);


interface ApprovalState {
    approvals: ApplicationOrderLineList | null
    approvalOrders: ApplicationOrderList | null
    awaitingApprovalCount: number,
    orderSuppliers: ApplicationSupplierHeader[],
    loading: boolean,
    productSuppliers: Record<number, ApplicationSupplierProductHeader[]>; // productId -> suppliers
    totals: ApplicationBasketTotals | null;
    selectedDepartment: ApplicationDepartment | null;
}

const initialState: ApprovalState = {
    approvals: null,
    approvalOrders: null,
    awaitingApprovalCount: 0,
    loading: false,
    orderSuppliers: [],
    productSuppliers: {},
    totals: null,
    selectedDepartment: null,
};


export interface UpdateOrderLinePayload {
    id: number;
    field: keyof ApplicationOrderLine;
    value: any;
}


const approvalSlice = createSlice({
    name: 'approvals',
    initialState,
    reducers: {
        resetApprovals: (state) => {
            state.approvalOrders = initialState.approvalOrders;
            state.approvals = initialState.approvals;
            state.totals = initialState.totals;
        },
        resetApprovalLines: (
            state
        ) => {
            state.approvals = initialState.approvals
        },
        setSelectedDepartment: (
            state,
            action: PayloadAction<ApplicationDepartment>
        ) => {
            state.selectedDepartment = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchApprovalOrders.fulfilled, (state, action: PayloadAction<ApplicationOrderList | null>) => {
                if (action.payload) {
                    if (state.approvalOrders?.orders) {
                        state.approvalOrders.orders = state.approvalOrders.orders.concat(action.payload!.orders);
                        state.approvalOrders.currentPosition = action.payload!.currentPosition
                        state.approvalOrders.count = action.payload!.count;
                    } else {
                        state.approvalOrders = action.payload;
                    }
                }
            })
            .addCase(fetchApprovals.fulfilled, (state, action: PayloadAction<ApplicationOrderLineList | null>) => {
                if (action.payload) {
                    if (state.approvals?.orderLines) {
                        state.approvals.orderLines = state.approvals.orderLines.concat(action.payload!.orderLines);
                        state.approvals.currentPosition = action.payload!.currentPosition
                        state.approvals.count = action.payload!.count;
                    } else {
                        state.approvals = action.payload;
                    }
                }
            })
            .addCase(getAwaitingApprovalCount.fulfilled, (state, action: PayloadAction<number>) => {
                state.awaitingApprovalCount = action.payload;
            })
            .addCase(fetchApprovalTotals.fulfilled, (state, action: PayloadAction<ApplicationBasketTotals>) => {
                state.totals = action.payload;
            })
            .addCase(fetchOrderSuppliers.fulfilled, (state, action: PayloadAction<ApplicationSupplierHeader[]>) => {
                if (action.payload) {
                    state.orderSuppliers = action.payload;
                }
            })
            .addCase(updateApproval.fulfilled, (state, action: PayloadAction<ApplicationOrderLine>) => {
                if (state.approvals) {
                    const index = state.approvals.orderLines.findIndex(item => item.id === action.payload.id);
                    if (index !== -1) {
                        state.approvals.orderLines[index] = action.payload;
                    }
                }
            })
            .addCase(createSplit.fulfilled, (state, action: PayloadAction<ApplicationOrderLine>) => {
                if (state.approvals) {
                    const index = state.approvals.orderLines.findIndex(item => item.id === action.payload.id);
                    if (index !== -1) {
                        state.approvals.orderLines[index] = action.payload;
                    }
                }
            })
            .addCase(
                fetchProductSuppliers.fulfilled,
                (state, action: PayloadAction<Record<number, ApplicationSupplierProductHeader[]> | null, string, { arg: { branchId: number; productIds: number[] } | { branchId: number; productId: number } }>) => {
                    const payload = action.payload ?? {};
                    Object.entries(payload).forEach(([productId, suppliers]) => {
                        state.productSuppliers[Number(productId)] = suppliers;
                    });
                }
            )
            .addCase(
                approveOrder.fulfilled,
                (state, action: PayloadAction<ApplicationOrder>) => {
                    if (action.payload && state.approvalOrders?.orders) {
                        state.approvalOrders.orders = state.approvalOrders.orders.filter(
                            (order) => order.id !== action.payload.id
                        );
                        state.approvalOrders.count -= 1;
                        state.approvalOrders.currentPosition -= 1;
                    }

                    state.loading = false
                }
            )
            .addCase(approveOrder.pending, handlePending)
            .addCase(approveOrder.rejected, handleError)
    },
});

const handlePending = (state: ApprovalState) => {
    state.loading = true;
};

const handleError = (state: ApprovalState) => {
    state.loading = false;
};

export const { resetApprovals, resetApprovalLines, setSelectedDepartment } = approvalSlice.actions;
export default approvalSlice.reducer;
